import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, MapPin, Package, CheckCircle, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';

const LocationAssignment = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [locationLocked, setLocationLocked] = useState(false);
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const staffInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
      setTimeout(() => locationInputRef.current?.focus(), 100);
    }
  };

  const handleUnlockStaff = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setStaffName('');
  };

  const handleLockLocation = () => {
    if (!locationId.trim()) {
      toast.error('Please enter Location ID');
      return;
    }
    setLocationLocked(true);
    setTimeout(() => bundleInputRef.current?.focus(), 100);
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    
    const code = bundleBarcode.trim();
    if (!code) {
      toast.error('Please scan bundle barcode');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    // Check if already scanned
    if (scannedBundles.find(b => b.barcode === code)) {
      toast.error('Bundle already scanned');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiClient.post(`/goods-transfer/location-assignment/validate-bundle?bundle_barcode=${encodeURIComponent(code)}`);
      
      if (response.data.valid) {
        setScannedBundles(prev => [{
          barcode: code,
          source: response.data.source,
          fg_info: response.data.fg_info,
          current_location: response.data.current_location
        }, ...prev]);
        toast.success(`Bundle added! Total: ${scannedBundles.length + 1}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid bundle';
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
      setBundleBarcode('');
      // Use setTimeout to ensure focus after state updates
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== barcode));
    toast.success('Bundle removed');
  };

  const handleSubmit = async () => {
    if (scannedBundles.length === 0) {
      toast.error('No bundles scanned');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/goods-transfer/location-assignment/submit', {
        staff_id: staffId,
        location_id: locationId,
        bundle_barcodes: scannedBundles.map(b => b.barcode)
      });
      
      toast.success(response.data.message);
      
      // Reset for next batch
      setScannedBundles([]);
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch (error) {
      console.error('Submit error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : 'Failed to submit');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/goods-transfer')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Operation 3: Location Assignment</h1>
              <p className="text-sm text-[#9CA3AF]">Assign storage location to received bundles</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Step 1: Staff ID */}
        <Card className="bg-white border-2 border-orange-200 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <User className="w-5 h-5 text-orange-500" />
              Staff Identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput
                value={staffId}
                onChange={setStaffId}
                onValidated={handleStaffValidated}
                placeholder="Scan or enter Staff ID..."
                autoFocus
                showAutocomplete={true}
                label=""
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-lg">
                  <span className="font-mono font-semibold">{staffId}</span>
                  {staffName && <span className="ml-2 text-orange-600">- {staffName}</span>}
                </div>
                <Button variant="outline" size="sm" onClick={handleUnlockStaff}>
                  Change
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Location */}
        {staffIdLocked && (
          <Card className="bg-white border-2 border-purple-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <MapPin className="w-5 h-5 text-purple-500" />
                Storage Location
              </CardTitle>
              <CardDescription>Enter the location ID where bundles will be stored</CardDescription>
            </CardHeader>
            <CardContent>
              {!locationLocked ? (
                <div className="flex gap-2">
                  <Input
                    ref={locationInputRef}
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    placeholder="Enter Location ID (e.g., RACK-A1-01)..."
                    className="text-lg h-12 font-mono"
                    onKeyDown={(e) => e.key === 'Enter' && handleLockLocation()}
                  />
                  <Button onClick={handleLockLocation} className="bg-purple-500 hover:bg-purple-600 h-12 px-6">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Lock
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-purple-500" />
                    <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-lg font-mono font-semibold">
                      {locationId}
                    </span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setLocationLocked(false)}>
                    Change
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Scan Bundles */}
        {locationLocked && (
          <Card className="bg-white border-2 border-blue-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <Package className="w-5 h-5 text-blue-500" />
                Scan Bundles
              </CardTitle>
              <CardDescription>Scan bundle barcodes to assign to this location</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBundleScan} className="flex gap-2">
                <Input
                  ref={bundleInputRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  placeholder="Scan Bundle Barcode..."
                  className="text-lg h-12 font-mono"
                  disabled={isProcessing}
                  autoComplete="off"
                />
                <Button type="submit" disabled={isProcessing} className="bg-blue-500 hover:bg-blue-600 h-12 px-6">
                  {isProcessing ? 'Validating...' : 'Add'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Scanned Bundles List */}
        {scannedBundles.length > 0 && (
          <Card className="bg-white border-none shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Scanned Bundles ({scannedBundles.length})
                </CardTitle>
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Assigning...' : 'Assign Location'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scannedBundles.map((bundle, index) => (
                  <div key={bundle.barcode} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-8">#{scannedBundles.length - index}</span>
                      <div>
                        <span className="font-mono font-semibold">{bundle.barcode}</span>
                        {bundle.fg_info && (
                          <p className="text-xs text-gray-500">
                            FG: {bundle.fg_info.design_name} | {bundle.fg_info.shade} | {bundle.fg_info.size}
                          </p>
                        )}
                        {bundle.current_location && (
                          <p className="text-xs text-orange-600">
                            Current: {bundle.current_location}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleRemoveBundle(bundle.barcode)}
                      className="text-red-500 hover:bg-red-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default LocationAssignment;
